import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React from 'react';
import DatePickerPanel from '../../components/DatePickerPanel';

// Without this, `dayjs(value, 'YYYY-MM-DD')` SILENTLY IGNORES the format string and falls back to
// Date parsing — it does not warn, it just returns a different date. moment honoured the format, so
// the helpers below need the plugin to behave the way their callers already expect (H1MVP-4742).
// `extend` is global and idempotent; this module is eager via the helpers barrel, so every other
// dayjs caller in the app gets the same parsing behaviour.
dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  'YYYY-MM-DDTHH:mm:ss',
  'YYYY-MM-DD',
  'YYYY-MM',
];

export const dateHelpers = {
  /**
   * Returns the current date decomposed into a dayjs object, year, month, and day.
   * @returns {{date: dayjs.Dayjs, year: number, month: number, day: number}} Current date parts.
   * @example
   * const { year, month, day } = dateHelpers.getCurrentDate();
   */
  getCurrentDate: () => {
    const date = dayjs(new Date());
    const year = parseInt(date.format('YYYY'));
    const month = parseInt(date.format('M'));
    const day = parseInt(date.format('D'));
    return { date, year, month, day };
  },
  /**
   * Parses a YYYY-MM-DD date string and returns it formatted with the given format.
   * @param {string|Date} date - Input date value parseable as YYYY-MM-DD.
   * @param {string} format - dayjs format string for the output (e.g. 'DD/MM/YYYY').
   * @returns {string} Formatted date string.
   * @example
   * dateHelpers.getDate('2024-03-15', 'DD/MM/YYYY'); // '15/03/2024'
   */
  getDate: (date, format) => dayjs(date, DATE_FORMATS).format(format),
  /**
   * Parses an ISO 8601 datetime string and returns it formatted with the given format.
   * @param {string} date - ISO 8601 datetime string.
   * @param {string} format - dayjs format string for the output.
   * @returns {string} Formatted date string.
   * @example
   * dateHelpers.getDateFromISO('2024-03-15T10:30:00Z', 'DD MMM YYYY'); // '15 Mar 2024'
   */
  getDateFromISO: (date, format) => dayjs(date, DATE_FORMATS).format(format),
  /**
   * Parses a YYYY-MM-DD date string and returns it formatted with a default of 'DD MMM YYYY'.
   * @param {string} date - Input date string in YYYY-MM-DD format.
   * @param {string} [format='DD MMM YYYY'] - dayjs format string for the output.
   * @returns {string} Formatted date string, e.g. '15 Mar 2024'.
   * @example
   * dateHelpers.getDateWithMonth('2024-03-15'); // '15 Mar 2024'
   */
  getDateWithMonth: (date, format = 'DD MMM YYYY') => dayjs(date, DATE_FORMATS).format(format),
  /**
   * Returns a Date object adjusted to remove the local timezone offset (normalises to GMT).
   * @param {string|Date} date - Date value to normalise.
   * @returns {Date} Date object shifted by the local timezone offset.
   * @example
   * dateHelpers.getDateWithGMT('2024-03-15T00:00:00+02:00');
   */
  getDateWithGMT: (date) => {
    let newDate = new Date(date);
    newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
    return newDate;
  },
  /**
   * Normalises a date to GMT and formats it with the given format string.
   * @param {string|Date} date - Date value to normalise and format.
   * @param {string} format - dayjs format string for the output.
   * @returns {string} Formatted GMT-normalised date string.
   * @example
   * dateHelpers.getDateWithGMTFormat('2024-03-15T00:00:00+02:00', 'DD MMM YYYY');
   */
  getDateWithGMTFormat: (date, format) => dateHelpers.getDate(dateHelpers.getDateWithGMT(date), format),
  /**
   * Formats a Unix timestamp (seconds or milliseconds) or date string into the given format.
   * @param {number|string} date - Unix timestamp in seconds or milliseconds, or a numeric date string.
   * @param {string} format - dayjs format string for the output.
   * @returns {string} Formatted date string.
   * @example
   * dateHelpers.getTimestampDate(1710499200, 'DD MMM YYYY'); // '15 Mar 2024'
   */
  getTimestampDate: (date, format) => {
    date = String(date).split('.')[0];
    if (date.length === 10) date = date * 1000;
    return dayjs(date).format(format);
  },
  /**
   * Converts a Unix timestamp (seconds or milliseconds) or date string to a JavaScript Date object.
   * @param {number|string} date - Unix timestamp in seconds or milliseconds, or a numeric date string.
   * @returns {Date} JavaScript Date object.
   * @example
   * dateHelpers.getTimestampDateObject(1710499200);
   */
  getTimestampDateObject: (date) => {
    date = String(date).split('.')[0];
    if (date.length === 10) date = date * 1000;
    return dayjs(date).toDate();
  },
  /**
   * Formats a Unix timestamp given in seconds. Unlike `getDate`, it does not run the value through
   * the string-format parser, which turns a `Date` object into 'Invalid Date'.
   * @param {number|string} seconds - Unix timestamp in seconds.
   * @param {string} format - dayjs format string for the output.
   * @returns {string} Formatted date string.
   * @example
   * dateHelpers.getDateFromUnix(1710499200, 'DD/MM/YY'); // '15/03/24'
   */
  getDateFromUnix: (seconds, format) => dayjs.unix(Number(seconds)).format(format),
  /**
   * Returns the Unix timestamp (seconds) for a dayjs object, or undefined if the value has no unix().
   * @param {dayjs.Dayjs|*} date - dayjs object or any other value.
   * @returns {number|undefined} Unix timestamp in seconds or undefined.
   * @example
   * dateHelpers.getMomentUnixTimestamp(dayjs()); // 1710499200
   */
  getMomentUnixTimestamp: (date) => (date && date.unix instanceof Function ? date.unix() : undefined),
  /**
   * Formats a duration in seconds as a 'mm:ss' string.
   * @param {number} seconds - Duration in seconds.
   * @returns {string} Formatted time string, e.g. '02:35'.
   * @example
   * dateHelpers.getSecondsTime(155); // '02:35'
   */
  getSecondsTime: (seconds) => {
    const date = new Date(0);
    date.setSeconds(seconds);
    return dayjs(date).format('mm:ss');
  },
  /**
   * Returns the date picker panel wrapped in a custom DatePickerPanel component.
   * @param {React.ReactNode} panelNode - The default panel node provided by Ant Design DatePicker.
   * @returns {React.ReactElement} Wrapped panel element.
   * @example
   * // Used as: panelRender={dateHelpers.getDatePickerPanelRender}
   * dateHelpers.getDatePickerPanelRender(panelNode);
   */
  getDatePickerPanelRender: (panelNode) => (
    <DatePickerPanel>
      {panelNode}
    </DatePickerPanel>
  ),
  /**
   * Advances a dayjs date to the next Monday if it falls on a Saturday or Sunday.
   * @param {dayjs.Dayjs|undefined} date - dayjs date object to adjust.
   * @returns {dayjs.Dayjs|undefined} Adjusted working day date, or undefined if input is falsy.
   * @example
   * dateHelpers.getNextWorkingDay(dayjs('2024-03-16')); // Saturday → dayjs('2024-03-18')
   */
  getNextWorkingDay: (date) => {
    if (!date) return undefined;

    let result = date;

    if (result.day() === 6) {
      result = result.add(2, 'day');
    } else if (result.day() === 0) {
      result = result.add(1, 'day');
    }

    return result;
  },
  /**
   * Returns the number of full months between today and a target date, plus 2 to include both boundary months.
   * @param {string|Date|dayjs.Dayjs} targetDate - Target date to calculate months from.
   * @returns {number} Number of full months plus 2.
   * @example
   * dateHelpers.getFullMonthsFromToday('2024-01-01'); // e.g. 4 if today is 2024-03-15
   */
  getFullMonthsFromToday: (targetDate) => {
    const today = dayjs().startOf('day');
    const target = dayjs(targetDate).startOf('day');

    // Calculate full months difference and add 2 to include current & target months
    return today.diff(target, 'month') + 2;
  },
  /**
   * Returns the exact number of calendar months between today and a target date.
   * Unlike getFullMonthsFromToday, this lands on the exact calendar month (day-agnostic),
   * matching the subtractMonthsCount that subscriptionsHelpers.getYearMonthQueryParams expects.
   * @param {string|Date|dayjs.Dayjs} targetDate - Target date (YYYY-MM-DD).
   * @returns {number} Calendar-month difference (0 for the current month, 1 for last month, ...).
   * @example
   * dateHelpers.getCalendarMonthsFromToday('2026-06-24'); // 1 if today is in 2026-07
   */
  getCalendarMonthsFromToday: (targetDate) => {
    const now = dayjs();
    const target = dayjs(targetDate, 'YYYY-MM-DD');
    return (now.year() - target.year()) * 12 + (now.month() - target.month());
  }
}
